import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import { Invitation, Profile } from '../../stores/reducers/invitations';
import { ConnectedUser } from '../../connection/socket';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { DRAG_TYPE, DropCallback } from '../../stores/reducers/user-interface';
import useShots from '../../contexts/shots/provider';
import EmptyScreenShare from './empty-screen-share';
import { appLogger } from '../../utils/datadog';
import { ROOM_STATES } from '../../stores/reducers/settings';

interface Props {
	video: VideoStream | undefined;
	feedPosition: number;
	email: string;
	dropCallback: DropCallback
}

export default function FeedScreenShare(props: Props) {
	const {
		video,
		feedPosition,
		email,
		dropCallback,
	} = props;

	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const { idsOfVideosInCanvas } = useShots();
	const videoElRef = useRef<HTMLVideoElement>(null);

	const isOnCanvas = useMemo(
		() => video && idsOfVideosInCanvas.includes(video.id),
		[video, idsOfVideosInCanvas],
	);

	const userFromEmail = useMemo(() => {
		const connectedUser = connectedUsers.find((user: ConnectedUser) => user.email === email);
		if (connectedUser) return connectedUser;
		const invite = invitations.find((invitation: Invitation) => invitation.profile.email === email);
		if (invite) {
			return invite.profile;
		}
		return {
			email: '',
			first_name: '',
			last_name: '',
			avatar: '',
			poster: '',
			job_title: '',
			gr_user: 0,
		} as Profile;
	}, [email, connectedUsers, invitations]);

	const { isOver, dropRef } = useDropArea({
		accepts: DRAG_TYPE.FEEDS,
		dropCallback,
		dropAreaData: { feedPosition },
	});

	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			videoElRef.current.srcObject = video.srcObject;
			videoElRef.current.volume = 0;
			if (!video.srcObject.active) return;
			videoElRef.current.play().then(() => {
				if (videoElRef.current?.paused) {
					videoElRef.current.play().catch((e) => {
						appLogger.error('video el play request error', {
							location: 'feed-screenshare second',
						}, e);
					});
				}
			}).catch((e) => {
				appLogger.error('video el play request error', {
					location: 'feed-screenshare first',
				}, e);
			});
			cleanupVideoStreamElement(videoElRef.current);
		}
	}, [video?.srcObject]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (
		<div ref={dropRef} className='feed-drop-area'>
			{video && video?.srcObject.active
				? <>
					{isOver && <div className="dnd-highlighted" />}
					<video
						key={video.srcObject.id + feedPosition}
						muted={true}
						autoPlay={true}
						poster={blackImage}
						playsInline
						ref={videoElRef}
					>
					</video>
			</>
				: <>
				{isOver && <div className="dnd-highlighted" />}
					<EmptyScreenShare profile={userFromEmail} />
				</>}
		</div>
	);
}
